// Admin-panel data hooks. One file per resource, following the existing
// query/mutation conventions (array queryKeys prefixed ["admin", ...], onSuccess
// invalidation). All routes are gated server-side by requireRole("admin"); these
// hooks are UX only. See docs/superpowers/specs/2026-08-31-admin-panel-design.md.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { buildListQuery } from "../../api/listQuery";
import { ME_QUERY_KEY } from "./useMe";

// Root key: every admin query hangs off this so a single invalidation refreshes
// the dashboard + user list + detail after any mutation.
export const ADMIN_QUERY_KEY = ["admin"];

// GET /admin/stats -> dashboard aggregates.
export const useAdminStats = (options = {}) =>
  useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => apiFetch("/admin/stats"),
    staleTime: 60_000,
    ...options,
  });

// GET /admin/users -> standard { items, total, page, pageSize, pages } envelope.
// Filters: search, role, is_active, sport, plus sort/order.
export const useAdminUsers = (params = {}, options = {}) =>
  useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () =>
      apiFetch("/admin/users", {
        query: buildListQuery(
          params.page ?? 1,
          {
            sort: params.sort ?? "created_at",
            order: params.order ?? "desc",
            search: params.search,
            role: params.role,
            is_active: params.is_active,
            sport: params.sport,
          },
          { pageSize: params.pageSize ?? 20 },
        ),
      }),
    ...options,
  });

// GET /admin/users/:id -> { user, role, athlete_profile, scout_profile,
// latest_athlete_tracking, latest_scout_tracking, audit }.
export const useAdminUser = (id, options = {}) =>
  useQuery({
    queryKey: ["admin", "users", "detail", id],
    queryFn: () => apiFetch(`/admin/users/${id}`),
    enabled: Boolean(id) && (options.enabled ?? true),
    ...options,
  });

// POST /admin/users/invite -> creates the account + profile, sends invite email.
export const useInviteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, name, role }) =>
      apiFetch("/admin/users/invite", {
        method: "POST",
        body: { email, name, role },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};

// PATCH /admin/users/:id -> edit core account + profile fields.
export const useUpdateAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) =>
      apiFetch(`/admin/users/${id}`, { method: "PATCH", body: fields }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
};

// POST /admin/users/:id/role -> scout <-> athlete swap.
export const useSwapRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }) =>
      apiFetch(`/admin/users/${id}/role`, { method: "POST", body: { role } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
};

// POST /admin/users/:id/activate | /deactivate -> soft delete toggle.
export const useSetUserActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }) =>
      apiFetch(`/admin/users/${id}/${active ? "activate" : "deactivate"}`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
};

// PATCH /admin/users/:id/tracking/by-date/:date -> admin-scoped section merge
// on an athlete's daily tracking. The athlete-facing endpoint is session-scoped
// and rejects non-athlete callers, so admin metric edits go through this twin.
// Returns a mutateAsync compatible with AthleteForm's `patchTrackingFn` prop
// ({ date, metrics, notes } -> saved row).
export const useAdminPatchTracking = (userId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ date, metrics, notes }) =>
      apiFetch(`/admin/users/${userId}/tracking/by-date/${date}`, {
        method: "PATCH",
        body: { metrics, notes },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};

// DELETE /admin/users/:id -> hard delete (cascade). Explicit, typed-confirmation.
export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => apiFetch(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};

// ---- Academias (DEV-51) ------------------------------------------------------
// La verificación de academia (scouts.academy_verified_at) abre el plantel de
// esa academia — y con él los datos de contacto de sus deportistas
// confirmados. Nunca se la da el propio scout: la da el equipo de Athletain.

// GET /admin/academy-members?status=unverified|verified|all -> { items }.
// Cada fila: scout_id, full_name, email, organization_name, academy_id,
// academy_name, is_academy_creator, academy_verified_at, created_at,
// confirmed_roster_count, academy_pending_count.
export const useAdminAcademyMembers = (status = "unverified", options = {}) =>
  useQuery({
    queryKey: ["admin", "academy-members", status],
    queryFn: () => apiFetch("/admin/academy-members", { query: { status } }),
    select: (data) => data?.items || [],
    ...options,
  });

// POST /admin/academy-members/:scoutId/verify { verified } -> verifica o quita.
export const useVerifyAcademyMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scoutId, verified }) =>
      apiFetch(`/admin/academy-members/${scoutId}/verify`, { method: "POST", body: { verified } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};

// POST /admin/academies/:id/confirm-pending -> { confirmed }. Confirma todos los
// ingresos pendientes de una academia (p. ej. un lote verificado fuera de la app).
export const useConfirmAcademyPending = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ academyId }) =>
      apiFetch(`/admin/academies/${academyId}/confirm-pending`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};
